import React, { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "../../api/apiClient";
import { formatWeightFromGrams } from "../../utils/weightUtils";
import { useConfirm } from "../../context/confirmContext";
import { useEscapeKey } from "../ui/useEscapeKey";
import RequestProductsModal from "../RequestProductsModal/RequestProductsModal";
import "./LocationTransfersDrawer.css";

const WEIGHT_UNITS = ["g", "oz", "lb"];

function parseFormattedWeight(formatted) {
    if (!formatted) return { value: "", unit: "lb" };
    const parts = String(formatted).trim().split(" ");
    return { value: parts[0] || "", unit: parts[1] || "lb" };
}

function formatDate(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr); 
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatDateFull(dateStr) {
    if (!dateStr) return "—"; 
    const d = new Date(dateStr); 
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}  

// ── Main Component ──────────────────────────────────────────────────────────

const LocationTransfersDrawer = ({
    licenseNo,
    locations = [],
    canManage = false,
    isLocationScoped = false,
    locationId,
    locationName,
    onClose,
    onCountChange,
    onProductsRequested,
}) => {
    const confirm = useConfirm();
    useEscapeKey(onClose);

    const [tab, setTab] = useState("requests");
    const [showRequestModal, setShowRequestModal] = useState(false);

    // ── Requests state ──
    const [requests, setRequests] = useState([]);
    const [reqLoading, setReqLoading] = useState(true);
    const [reqError, setReqError] = useState("");
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [editWeights, setEditWeights] = useState({});
    const [editNotes, setEditNotes] = useState({});
    const [rowState, setRowState] = useState({});

    // ── History state ──
    const [history, setHistory] = useState([]);
    const [histLoading, setHistLoading] = useState(false);
    const [histTotal, setHistTotal] = useState(0);
    const [histPage, setHistPage] = useState(1);
    const [histTotalPages, setHistTotalPages] = useState(1);
    const [histFilters, setHistFilters] = useState({
        search: "",
        locationId: "",
        status: "",
        startDate: "",
        endDate: "",
    });
    const histSearchRef = useRef(null);

    // ── Fetch pending requests ──
    const fetchRequests = useCallback(async () => {
        if (!licenseNo) return;
        setReqLoading(true);
        setReqError("");
        try {
            const res = await apiClient.get(`/location-requests?licenseNo=${licenseNo}&status=requested`);
            const data = Array.isArray(res.data?.requests) ? res.data.requests : [];
            setRequests(data);
            const weights = {};
            data.forEach((r) => {
                const parsed = parseFormattedWeight(formatWeightFromGrams(parseFloat(r.quantity_grams)));
                weights[r.id] = { value: parsed.value, unit: parsed.unit };
            });
            setEditWeights(weights);
            setEditNotes({});
            setRowState({});
            setSelectedIds(new Set());
            if (onCountChange) onCountChange(data.length);
        } catch {
            setReqError("Failed to load requests.");
        } finally {
            setReqLoading(false);
        }
    }, [licenseNo, onCountChange]);

    // ── Fetch history ──
    const fetchHistory = useCallback(async () => {
        if (!licenseNo) return;
        setHistLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("licenseNo", licenseNo);
            params.set("page", histPage);
            params.set("limit", 25);
            if (histFilters.search) params.set("search", histFilters.search);
            if (histFilters.locationId) params.set("locationId", histFilters.locationId);
            if (histFilters.status) params.set("status", histFilters.status);
            if (histFilters.startDate) params.set("startDate", histFilters.startDate);
            if (histFilters.endDate) params.set("endDate", histFilters.endDate);

            const res = await apiClient.get(`/location-requests/history?${params.toString()}`);
            setHistory(res.data?.rows || []);
            setHistTotal(res.data?.total || 0);
            setHistTotalPages(res.data?.totalPages || 1);
        } catch {
            setHistory([]);
        } finally {
            setHistLoading(false);
        }
    }, [licenseNo, histPage, histFilters]);

    useEffect(() => { fetchRequests(); }, [fetchRequests]);
    useEffect(() => {
        if (tab === "history") fetchHistory();
    }, [tab, fetchHistory]);

    // ── Selection helpers ──
    const toggleSelected = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        if (selectedIds.size === requests.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(requests.map((r) => r.id)));
        }
    };

    // ── Single approve ──
    const handleApprove = async (requestId) => {
        const ok = await confirm({
            title: "Approve Request",
            message: "Approve this location transfer request?",
            confirmLabel: "Approve",
            variant: "primary",
        });
        if (!ok) return;

        setRowState((prev) => ({ ...prev, [requestId]: { processing: true } }));

        const weightData = editWeights[requestId];
        const notes = editNotes[requestId] || "";

        try {
            const body = { notes };
            if (weightData) {
                const { toGrams } = await import("../../utils/weightUtils");
                body.weight_grams = toGrams(parseFloat(weightData.value), weightData.unit);
            }
            await apiClient.patch(`/location-requests/${requestId}/approve`, body);
            await fetchRequests();
        } catch (err) {
            setRowState((prev) => ({
                ...prev,
                [requestId]: { processing: false, error: err.response?.data?.error || "Approval failed" },
            }));
        }
    };

    // ── Single deny ──
    const handleDeny = async (requestId) => {
        const ok = await confirm({
            title: "Deny Request",
            message: "Deny this location transfer request?",
            confirmLabel: "Deny",
            variant: "danger",
        });
        if (!ok) return;

        setRowState((prev) => ({ ...prev, [requestId]: { processing: true } }));
        const notes = editNotes[requestId] || "";

        try {
            await apiClient.patch(`/location-requests/${requestId}/deny`, { notes });
            await fetchRequests();
        } catch (err) {
            setRowState((prev) => ({
                ...prev,
                [requestId]: { processing: false, error: err.response?.data?.error || "Deny failed" },
            }));
        }
    };

    // ── Bulk approve ──
    const handleBulkApprove = async () => {
        if (selectedIds.size === 0) return;
        const ok = await confirm({
            title: "Bulk Approve",
            message: `Approve ${selectedIds.size} selected request(s)?`,
            confirmLabel: "Approve All",
            variant: "primary",
        });
        if (!ok) return;

        try {
            await apiClient.post("/location-requests/bulk-approve", {
                ids: Array.from(selectedIds),
                licenseNo,
            });
            await fetchRequests();
        } catch {
            setReqError("Bulk approval failed.");
        }
    };

    // ── Bulk deny ──
    const handleBulkDeny = async () => {
        if (selectedIds.size === 0) return;
        const ok = await confirm({
            title: "Bulk Deny",
            message: `Deny ${selectedIds.size} selected request(s)?`,
            confirmLabel: "Deny All",
            variant: "danger",
        });
        if (!ok) return;

        try {
            await apiClient.post("/location-requests/bulk-deny", {
                ids: Array.from(selectedIds),
                licenseNo,
            });
            await fetchRequests();
        } catch {
            setReqError("Bulk deny failed.");
        }
    };

    // ── CSV export ──
    const handleExport = async () => {
        const params = new URLSearchParams();
        params.set("licenseNo", licenseNo);
        if (histFilters.search) params.set("search", histFilters.search);
        if (histFilters.locationId) params.set("locationId", histFilters.locationId);
        if (histFilters.status) params.set("status", histFilters.status);
        if (histFilters.startDate) params.set("startDate", histFilters.startDate);
        if (histFilters.endDate) params.set("endDate", histFilters.endDate);

        try {
            const res = await apiClient.get(`/location-requests/history/export?${params.toString()}`, {
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = "location-transfers.csv";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch {
            // silent fail on export
        }
    };

    // ── History filter change ──
    const updateFilter = (key, value) => {
        setHistFilters((prev) => ({ ...prev, [key]: value }));
        setHistPage(1);
    };

    // ── Render ──────────────────────────────────────────────────────────────

    const pendingCount = requests.length;

    return (
        <>
            <div className="loc-drawer-overlay" onClick={onClose} />
            <div className="loc-drawer" role="dialog" aria-modal="true" aria-label="Managed Transfers">
                {/* Header */}
                <div className="loc-drawer__header">
                    <h2 className="loc-drawer__title">Managed Transfers</h2>
                    <button className="loc-drawer__close" onClick={onClose} aria-label="Close">✕</button>
                </div>

                {/* Tabs — hidden for location-scoped (no history access) */}
                {!isLocationScoped && (
                    <div className="loc-drawer__tabs">
                        <button
                            className={`loc-drawer__tab ${tab === "requests" ? "loc-drawer__tab--active" : ""}`}
                            onClick={() => setTab("requests")}
                        >
                            Requests
                            {pendingCount > 0 && (
                                <span className="loc-drawer__tab-badge">{pendingCount}</span>
                            )}
                        </button>
                        <button
                            className={`loc-drawer__tab ${tab === "history" ? "loc-drawer__tab--active" : ""}`}
                            onClick={() => setTab("history")}
                        >
                            History
                        </button>
                    </div>
                )}

                {/* Subheader for location-scoped (replaces tabs) */}
                {isLocationScoped && (
                    <div className="loc-drawer__subheader">
                        <span className="loc-drawer__subheader-label">My Requests</span>
                        {pendingCount > 0 && (
                            <span className="loc-drawer__tab-badge">{pendingCount}</span>
                        )}
                    </div>
                )}

                {/* Content */}
                <div className="loc-drawer__content">
                    {(!isLocationScoped && tab === "history") ? (
                        <HistoryTab
                            history={history}
                            loading={histLoading}
                            total={histTotal}
                            page={histPage}
                            totalPages={histTotalPages}
                            filters={histFilters}
                            locations={locations}
                            searchRef={histSearchRef}
                            onFilterChange={updateFilter}
                            onPageChange={setHistPage}
                            onExport={handleExport}
                        />
                    ) : (
                        <RequestsTab
                            requests={requests}
                            loading={reqLoading}
                            error={reqError}
                            canManage={isLocationScoped ? false : canManage}
                            isLocationScoped={isLocationScoped}
                            selectedIds={selectedIds}
                            editWeights={editWeights}
                            editNotes={editNotes}
                            rowState={rowState}
                            onToggleSelected={toggleSelected}
                            onToggleAll={toggleAll}
                            onWeightChange={(id, field, val) =>
                                setEditWeights((prev) => ({
                                    ...prev,
                                    [id]: { ...prev[id], [field]: val },
                                }))
                            }
                            onNotesChange={(id, val) =>
                                setEditNotes((prev) => ({ ...prev, [id]: val }))
                            }
                            onApprove={handleApprove}
                            onDeny={handleDeny}
                            onBulkApprove={handleBulkApprove}
                            onBulkDeny={handleBulkDeny}
                        />
                    )}
                </div>

                {/* Footer — Request Products button (location-scoped only) */}
                {isLocationScoped && (
                    <div className="loc-drawer__footer">
                        <button
                            className="loc-drawer__request-btn"
                            onClick={() => setShowRequestModal(true)}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            Request Products
                        </button>
                    </div>
                )}
            </div>

            {showRequestModal && (
                <RequestProductsModal
                    licenseNo={licenseNo}
                    locationId={locationId}
                    locationName={locationName}
                    onClose={() => setShowRequestModal(false)}
                    onSubmitted={() => {
                        setShowRequestModal(false);
                        fetchRequests();
                        if (onProductsRequested) onProductsRequested();
                    }}
                />
            )}
        </>
    );
};

// ── Requests Tab ────────────────────────────────────────────────────────────

function RequestsTab({
    requests, loading, error, canManage, isLocationScoped,
    selectedIds, editWeights, editNotes, rowState,
    onToggleSelected, onToggleAll,
    onWeightChange, onNotesChange,
    onApprove, onDeny, onBulkApprove, onBulkDeny,
}) {
    if (loading) return <div className="loc-drawer__loading">Loading requests…</div>;
    if (error) return <div className="loc-drawer__empty" style={{ color: "var(--color-danger)" }}>{error}</div>;
    if (requests.length === 0) return <div className="loc-drawer__empty">No pending requests</div>;

    return (
        <>
            {isLocationScoped && !canManage && (
                <div className="loc-drawer__readonly-banner">
                    View only — your requests are shown below
                </div>
            )}

            {/* Bulk actions (admin only) */}
            {canManage && requests.length > 1 && (
                <div className="loc-drawer__bulk-bar">
                    <input
                        type="checkbox"
                        className="loc-drawer__bulk-check"
                        checked={selectedIds.size === requests.length && requests.length > 0}
                        onChange={onToggleAll}
                    />
                    <span className="loc-drawer__bulk-label">
                        {selectedIds.size > 0 ? `${selectedIds.size} selected` : "Select all"}
                    </span>
                    <button
                        className="loc-drawer__bulk-btn loc-drawer__bulk-btn--approve"
                        onClick={onBulkApprove}
                        disabled={selectedIds.size === 0}
                    >
                        Approve
                    </button>
                    <button
                        className="loc-drawer__bulk-btn loc-drawer__bulk-btn--deny"
                        onClick={onBulkDeny}
                        disabled={selectedIds.size === 0}
                    >
                        Deny
                    </button>
                </div>
            )}

            {requests.map((r) => {
                const state = rowState[r.id] || {};
                const weight = editWeights[r.id] || { value: "", unit: "lb" };
                const notes = editNotes[r.id] || "";
                const availFormatted = formatWeightFromGrams(parseFloat(r.available_quantity) || 0);

                return (
                    <div
                        key={r.id}
                        className={`loc-req-card ${state.processing ? "loc-req-card--processing" : ""}`}
                    >
                        <div className="loc-req-card__top">
                            {canManage && (
                                <input
                                    type="checkbox"
                                    className="loc-req-card__check"
                                    checked={selectedIds.has(r.id)}
                                    onChange={() => onToggleSelected(r.id)}
                                />
                            )}
                            <div className="loc-req-card__info">
                                <div className="loc-req-card__product">{r.product_name}</div>
                                <div className="loc-req-card__meta">
                                    <span>{r.requesting_location_name}</span>
                                    {r.requester_first_name && (
                                        <span>{r.requester_first_name} {r.requester_last_name}</span>
                                    )}
                                    <span>{formatDate(r.created_at)}</span>
                                </div>
                            </div>
                            <span className="loc-status-pill loc-status-pill--pending">Pending</span>
                        </div>

                        {canManage ? (
                            <>
                                <div className="loc-req-card__qty-row">
                                    <span className="loc-req-card__qty-label">Fulfill</span>
                                    <input
                                        type="number"
                                        className="loc-req-card__qty-input"
                                        value={weight.value}
                                        onChange={(e) => onWeightChange(r.id, "value", e.target.value)}
                                        min="0"
                                        step="0.01"
                                    />
                                    <select
                                        className="loc-req-card__unit"
                                        value={weight.unit}
                                        onChange={(e) => onWeightChange(r.id, "unit", e.target.value)}
                                        style={{ background: "transparent", border: "none", color: "inherit", fontSize: "inherit" }}
                                    >
                                        {WEIGHT_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                    <span className="loc-req-card__avail">
                                        Avail: <strong>{availFormatted}</strong>
                                    </span>
                                </div>

                                <div className="loc-req-card__notes">
                                    <textarea
                                        className="loc-req-card__notes-input"
                                        rows="1"
                                        placeholder="Optional notes…"
                                        value={notes}
                                        onChange={(e) => onNotesChange(r.id, e.target.value)}
                                    />
                                </div>

                                <div className="loc-req-card__actions">
                                    <button
                                        className="loc-req-card__btn loc-req-card__btn--deny"
                                        onClick={() => onDeny(r.id)}
                                        disabled={state.processing}
                                    >
                                        Deny
                                    </button>
                                    <button
                                        className="loc-req-card__btn loc-req-card__btn--approve"
                                        onClick={() => onApprove(r.id)}
                                        disabled={state.processing}
                                    >
                                        Approve
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="loc-req-card__qty-row">
                                <span className="loc-req-card__qty-label">Requested</span>
                                <span style={{ fontSize: "var(--text-sm)", color: "var(--color-text)" }}>
                                    {r.quantity_requested} {r.weight_unit}
                                </span>
                            </div>
                        )}

                        {state.error && (
                            <div className="loc-req-card__error">{state.error}</div>
                        )}
                    </div>
                );
            })}
        </>
    );
}

// ── History Tab ─────────────────────────────────────────────────────────────

function HistoryTab({
    history, loading, total, page, totalPages,
    filters, locations, searchRef,
    onFilterChange, onPageChange, onExport,
}) {
    const startIdx = (page - 1) * 25 + 1;
    const endIdx = Math.min(page * 25, total);

    return (
        <>
            {/* Filters */}
            <div className="loc-history__filters">
                <input
                    ref={searchRef}
                    type="text"
                    className="loc-history__filter-input"
                    placeholder="Search product…"
                    value={filters.search}
                    onChange={(e) => onFilterChange("search", e.target.value)}
                />
                <select
                    className="loc-history__filter-select"
                    value={filters.locationId}
                    onChange={(e) => onFilterChange("locationId", e.target.value)}
                >
                    <option value="">All locations</option>
                    {locations.map((loc) => (
                        <option key={loc.locationId} value={loc.locationId}>
                            {loc.locationName}
                        </option>
                    ))}
                </select>
                <select
                    className="loc-history__filter-select"
                    value={filters.status}
                    onChange={(e) => onFilterChange("status", e.target.value)}
                >
                    <option value="">All status</option>
                    <option value="approved">Approved</option>
                    <option value="denied">Denied</option>
                </select>
                <input
                    type="date"
                    className="loc-history__filter-input"
                    value={filters.startDate}
                    onChange={(e) => onFilterChange("startDate", e.target.value)}
                />
                <input
                    type="date"
                    className="loc-history__filter-input"
                    value={filters.endDate}
                    onChange={(e) => onFilterChange("endDate", e.target.value)}
                />
                <button className="loc-history__export-btn" onClick={onExport}>
                    ↓ CSV
                </button>
            </div>

            {/* Table */}
            {loading ? (
                <div className="loc-drawer__loading">Loading history…</div>
            ) : history.length === 0 ? (
                <div className="loc-drawer__empty">No transfer history found</div>
            ) : (
                <>
                    <div className="loc-history__table-wrap">
                        <table className="loc-history__table">
                            <thead>
                                <tr>
                                    <th>Product</th>
                                    <th>Location</th>
                                    <th>Qty</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((row) => (
                                    <tr key={row.id}>
                                        <td>{row.product_name}</td>
                                        <td>{row.requesting_location_name}</td>
                                        <td>{row.quantity_requested} {row.weight_unit}</td>
                                        <td>
                                            <span className={`loc-status-pill loc-status-pill--${row.status}`}>
                                                {row.status}
                                            </span>
                                        </td>
                                        <td>{formatDateFull(row.reviewed_at || row.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="loc-history__pagination">
                        <span>{startIdx}–{endIdx} of {total}</span>
                        <div className="loc-history__page-btns">
                            <button
                                className="loc-history__page-btn"
                                onClick={() => onPageChange(page - 1)}
                                disabled={page <= 1}
                            >
                                ←
                            </button>
                            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                let p;
                                if (totalPages <= 5) {
                                    p = i + 1;
                                } else if (page <= 3) {
                                    p = i + 1;
                                } else if (page >= totalPages - 2) {
                                    p = totalPages - 4 + i;
                                } else {
                                    p = page - 2 + i;
                                }
                                return (
                                    <button
                                        key={p}
                                        className={`loc-history__page-btn ${p === page ? "loc-history__page-btn--active" : ""}`}
                                        onClick={() => onPageChange(p)}
                                    >
                                        {p}
                                    </button>
                                );
                            })}
                            <button
                                className="loc-history__page-btn"
                                onClick={() => onPageChange(page + 1)}
                                disabled={page >= totalPages}
                            >
                                →
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}

export default LocationTransfersDrawer;
